import { t } from 'i18next';

import AdvancedFilters from 'components/desktop/Accessory/Search/Components/AdvancedFilters/AdvancedFilters';
import SearchResultList from 'components/desktop/Accessory/Search/Components/SearchResultList/SearchResultList';
import { useAccessorySearchUrl } from 'components/desktop/Accessory/Search/useAccessorySearchUrl';
import useSearchState from 'components/desktop/Accessory/Search/useSearchState';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import DropdownPageSize from 'components/desktop/_general/DropdownPageSize/DropdownPageSize';
import DropdownSortOrder from 'components/desktop/_general/DropdownSortOrder/DropdownSortOrder';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import { useCollapsibleHook } from 'hooks/useCollapsible';
import { usePermissions } from 'hooks/usePermissions';
import { useReaction } from 'hooks/useReaction';
import { ISelectItem } from 'interfaces/ISelectItem';
import * as AccessorySearchResponse from 'types/Accessory/Search/AccessorySearchResponseDto';
import * as Search from 'types/Accessory/Search/Search';

interface IProps {
	archive: boolean;
}

const maxNumberOfButtons = 10;

const AccessorySearchComponent = ({ archive }: IProps) => {
	const { isAccessoryEditor } = usePermissions();
	const { search, searchHandlers } = useAccessorySearchUrl(archive);
	const { state, handlers } = useSearchState({
		searchTerm: search.searchTerm,
		showAdvancedSearch: Search.hasAdvancedSearchParameters(search),
	});

	const [accessoryResponse, isLoaded] = useReaction<AccessorySearchResponse.Type, Search.Type>(
		AccessorySearchResponse.create(),
		AccessorySearchResponse.getFromApi,
		search
	);

	const collapse = useCollapsibleHook(Search.hasAdvancedSearchParameters(search));

	const getSuggestedAccessories = async (): Promise<ISelectItem[]> => {
		return AccessorySearchResponse.getFromApiSW(state.searchTerm, search.showOnlyDeleted);
	};

	return (
		<main className="container">
			<div className="d-flex justify-content-between">
				<RenderIf condition={!search.showOnlyDeleted}>
					<h1>{t('accessory:SEARCH_ACCESSORY')}</h1>
				</RenderIf>
				<RenderIf condition={search.showOnlyDeleted}>
					<h1>{t('accessory:ACCESSORY_ARCHIVE')}</h1>
				</RenderIf>
				<RenderIf condition={isAccessoryEditor}>
					<BtnNew link={'/accessory/new'} name={t('accessory:CREATE_ACCESSORY')} />
				</RenderIf>
			</div>
			<br />
			<div className="row">
				<div className="col-lg-7">
					<div className="input-group mb-3">
						<AdvancedSearchInput
							debounceMilliSeconds={350}
							getSuggestedItems={getSuggestedAccessories}
							handleSearch={(input) =>
								searchHandlers.changeSearchTerm(input ? input : state.searchTerm)
							}
							searchTerm={state.searchTerm}
							setSearchTerm={handlers.setSearchTerm}
							triggerSearchLetterAmount={1}
						/>
					</div>
				</div>
				<div className="col-lg-2">
					<button
						className="btn btn-primary"
						type="button"
						onClick={collapse.handleToggle}
					>
						{t('_general:ADVANCED_FILTER')}
					</button>
				</div>
				<div className="col-lg-3 text-end">
					<div className="d-flex flex-row justify-content-end">
						<DropdownPageSize
							className="me-2"
							listFunction={searchHandlers.changePageSize}
							currentValue={String(search.pageSize)}
						/>
						<DropdownSortOrder
							type={'accessory'}
							listFunction={searchHandlers.changeSortOrder}
							currentValue={search.sortOrder}
						/>
					</div>
				</div>
			</div>
			<div className={collapse.className} style={collapse.style} ref={collapse.ref}>
				<div className="card card-body mb-3">
					<AdvancedFilters
						archive={archive}
						disabled={accessoryResponse.totalCount === 0}
						handleChangeCategories={searchHandlers.changeCategories}
						handleChangeCharacteristics={searchHandlers.changeCharacteristics}
						handleChangeCo2Labels={searchHandlers.changeCo2Labels}
						handleChangeSliders={searchHandlers.changeSlider}
						handleChangeTags={searchHandlers.changeTags}
						handleReset={searchHandlers.reset}
						search={search}
						searchResponse={accessoryResponse}
						showAdvancedSearch={collapse.isOpen}
					/>
				</div>
			</div>
			<div className="mb-3" data-component-type="search-result-list">
				<RenderIf condition={isLoaded}>
					<SearchResultList
						archive={search.showOnlyDeleted}
						accessoryList={accessoryResponse.data}
						handleArchiveAccessory={searchHandlers.archive}
						handleRestoreAccessory={searchHandlers.restore}
					/>
				</RenderIf>
				<RenderIf condition={!isLoaded}>
					<LoadingAnimation isLoading={!isLoaded} />
				</RenderIf>
			</div>
			<Pagination
				countOfAllListItems={accessoryResponse.totalCount}
				pageIndex={search.pageIndex}
				setPageIndex={searchHandlers.changePageIndex}
				listItemAmount={search.pageSize}
				maxNumberOfButtons={maxNumberOfButtons}
			/>
		</main>
	);
};

export default AccessorySearchComponent;
